import { Check, ChevronDown, ChevronRight, Copy, FileText } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { contactName } from "@/lib/contacts";
import { useContactTranscripts, useFetchTranscriptLines, useTranscriptLines } from "@/lib/queries";
import {
  SOURCE_TITLES, SPEAKER_HINTS, SPEAKER_LABELS, copiedMessage, formatTranscripts, newestFirst,
  repLabel, transcriptKey, transcriptStamp, type TranscriptContact,
} from "@/lib/transcripts";
import type { LeadCard, TranscriptListItem, TranscriptSegment } from "@/lib/types";
import { cn, formatDateTime, formatDuration } from "@/lib/utils";

/**
 * The contact's Transcripts tab (ADR-081 in platform): the transcripts of their calls, to read and
 * to copy — one, the ones ticked, or all of them — without opening each call.
 *
 * **Whose calls.** core-api lists them under the rules that already decide who may read a call's
 * transcript, as the Calls tab next door lists legs: a rep sees their own calls with this contact,
 * a supervisor all of the tenant's.
 *
 * **A row is a transcript, not a call leg.** A recorded call is two or three legs on the Calls tab
 * and one transcript here. A call transcribed both ways, live and from its recording, is two rows,
 * as its details show the two passes as two sections.
 *
 * **What is copied is plain text**, laid out by `formatTranscripts`: a header naming the call, a
 * line per turn, and a divider between calls. It is pasted into CRM notes, emails and documents,
 * where markup would arrive as stray symbols.
 */
export function ContactTranscripts({ contact }: { contact: LeadCard }) {
  const toast = useToast();
  const list = useContactTranscripts(contact.id);
  const fetchLines = useFetchTranscriptLines();
  const [openKey, setOpenKey] = React.useState<string | null>(null);
  const [picked, setPicked] = React.useState<ReadonlySet<string>>(new Set());
  // What is being copied right now: "all", "picked", or one row's key.
  const [copying, setCopying] = React.useState<string | null>(null);
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!copiedKey) return;
    const t = setTimeout(() => setCopiedKey(null), 1500);
    return () => clearTimeout(t);
  }, [copiedKey]);

  const rows = React.useMemo(() => list.data?.items ?? [], [list.data]);
  const total = list.data?.total ?? null;
  const keys = rows.map(transcriptKey);
  // A tick survives only while its row is listed: a refetch that drops a transcript drops its tick.
  const ticked = rows.filter((item) => picked.has(transcriptKey(item)));
  const allTicked = rows.length > 0 && ticked.length === rows.length;
  const selectAll = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (selectAll.current) selectAll.current.indeterminate = ticked.length > 0 && !allTicked;
  }, [ticked.length, allTicked]);

  const who: TranscriptContact = { name: contactName(contact), phone: contact.phone };

  /**
   * Copy some transcripts, newest call first, and say how many went.
   *
   * The text is built in a promise handed straight to the clipboard, inside the press (see
   * `copyText`): a browser that lets a page write to the clipboard only in answer to a press counts
   * it as spent by the time the lines have been fetched. A copy that found nothing to put there
   * leaves the clipboard as it was, rather than emptying it.
   */
  async function copy(what: string, items: readonly TranscriptListItem[]): Promise<boolean> {
    const ordered = newestFirst(items);
    let copied = 0;
    const text = (async () => {
      const lines = await fetchLines(ordered);
      const docs = ordered.flatMap((item) => {
        const found = lines.get(transcriptKey(item));
        return found?.length ? [{ item, lines: found }] : [];
      });
      copied = docs.length;
      if (docs.length === 0) throw new NothingToCopy();
      return formatTranscripts(who, docs);
    })();
    setCopying(what);
    try {
      await copyText(text);
      const message = copiedMessage({ copied, asked: ordered.length });
      toast.success(message.title, message.description);
      return true;
    } catch (error) {
      if (error instanceof NothingToCopy) {
        const message = copiedMessage({ copied: 0, asked: ordered.length });
        toast.info(message.title, message.description);
      } else {
        toast.error(
          "Could not copy",
          error instanceof ApiError || error instanceof Error ? error.message : "Please try again.",
        );
      }
      return false;
    } finally {
      setCopying(null);
    }
  }

  async function copyOne(item: TranscriptListItem) {
    const key = transcriptKey(item);
    if (await copy(key, [item])) setCopiedKey(key);
  }

  function tick(item: TranscriptListItem, on: boolean) {
    setPicked((current) => {
      const next = new Set(current);
      if (on) next.add(transcriptKey(item));
      else next.delete(transcriptKey(item));
      return next;
    });
  }

  if (list.isPending) return <TableSkeleton cols={3} rows={4} />;
  if (list.error) return <ErrorState error={list.error} title="Could not load the transcripts" />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No transcripts yet"
        description="A call's transcript shows here a minute or so after the call ends, once its recording has been transcribed."
      />
    );
  }

  const busy = copying !== null;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <label className="inline-flex items-center gap-2 text-xs text-text-muted">
          <input
            ref={selectAll}
            type="checkbox"
            className="size-3.5 accent-accent-9"
            checked={allTicked}
            onChange={(e) => setPicked(e.target.checked ? new Set(keys) : new Set())}
          />
          {ticked.length > 0
            ? `${ticked.length} of ${rows.length} selected`
            : `${rows.length} transcript${rows.length === 1 ? "" : "s"}`}
        </label>
        <span className="ml-auto inline-flex items-center gap-2">
          {ticked.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => void copy("picked", ticked)} disabled={busy}>
              {copying === "picked" ? <Spinner /> : <Copy />}
              Copy selected ({ticked.length})
            </Button>
          )}
          <Button size="sm" variant="default" onClick={() => void copy("all", rows)} disabled={busy}>
            {copying === "all" ? <Spinner /> : <Copy />}
            Copy all
          </Button>
        </span>
      </div>
      {total !== null && total > rows.length && (
        <p className="border-b border-border px-4 py-2 text-xs text-text-muted">
          The newest {rows.length.toLocaleString()} of {total.toLocaleString()} are listed.
        </p>
      )}
      <ul className="divide-y divide-border">
        {rows.map((item) => {
          const key = transcriptKey(item);
          return (
            <TranscriptRow
              key={key}
              item={item}
              open={openKey === key}
              onToggle={() => setOpenKey(openKey === key ? null : key)}
              ticked={picked.has(key)}
              onTick={(on) => tick(item, on)}
              copying={copying === key}
              copied={copiedKey === key}
              disabled={busy}
              onCopy={() => void copyOne(item)}
            />
          );
        })}
      </ul>
    </div>
  );
}

/** A copy that found no lines to put on the clipboard, which it then leaves as it was. */
class NothingToCopy extends Error {}

/**
 * One transcript: a tick, the call it is of (a button that opens it in place), and Copy. Three
 * controls side by side rather than one row that is itself a button, so a tick or a copy never
 * also opens the row.
 */
function TranscriptRow({
  item, open, onToggle, ticked, onTick, copying, copied, disabled, onCopy,
}: {
  item: TranscriptListItem;
  open: boolean;
  onToggle: () => void;
  ticked: boolean;
  onTick: (on: boolean) => void;
  copying: boolean;
  copied: boolean;
  disabled: boolean;
  onCopy: () => void;
}) {
  const when = formatDateTime(item.started_at);
  const source = item.source === "recording" ? "recording" : "live";
  const details = [
    item.reps.length ? item.reps.map(repLabel).join(", ") : null,
    item.campaign_name ?? (item.campaign_id ? "Deleted campaign" : null),
  ].filter(Boolean);
  return (
    <li className={cn(open && "bg-surface-sunken")}>
      <div className="flex items-start gap-3 px-4 py-2.5">
        <input
          type="checkbox"
          className="mt-1 size-3.5 shrink-0 accent-accent-9"
          aria-label={`Select the ${source} transcript of the call at ${when}`}
          checked={ticked}
          onChange={(e) => onTick(e.target.checked)}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className={cn(
            "flex min-w-0 flex-1 items-start gap-1.5 rounded text-left",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-8",
          )}
        >
          {open ? (
            <ChevronDown className="mt-0.5 size-3.5 shrink-0 text-text-subtle" aria-hidden />
          ) : (
            <ChevronRight className="mt-0.5 size-3.5 shrink-0 text-text-subtle" aria-hidden />
          )}
          <span className="min-w-0 space-y-0.5">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text">
              {when}
              {item.duration_seconds !== null && (
                <span className="font-mono text-xs text-text-muted">{formatDuration(item.duration_seconds)}</span>
              )}
              <Badge tone={item.source === "recording" ? "accent" : "neutral"} size="sm">
                {item.source === "recording" ? "Recording" : "Live"}
              </Badge>
              <span className="text-xs text-text-muted">
                {item.line_count.toLocaleString()} line{item.line_count === 1 ? "" : "s"}
              </span>
            </span>
            {details.length > 0 && (
              <span className="block truncate text-xs text-text-muted">{details.join(" · ")}</span>
            )}
          </span>
        </button>
        <Tooltip content={copied ? "Copied" : "Copy this transcript"}>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onCopy}
            disabled={disabled}
            aria-label={copied ? "Copied" : `Copy the ${source} transcript of the call at ${when}`}
          >
            {copying ? <Spinner /> : copied ? <Check className="text-success-9" /> : <Copy />}
          </Button>
        </Tooltip>
      </div>
      {open && <TranscriptLinesPanel item={item} />}
    </li>
  );
}

/** One transcript read in place, under its row. Read only: the tab has no player, so a line here
 *  does not seek. The call's details have both. */
function TranscriptLinesPanel({ item }: { item: TranscriptListItem }) {
  const { data, isPending, error } = useTranscriptLines(item);
  return (
    <div className="px-4 pb-3 pl-11">
      <p className="mb-1 text-2xs font-medium uppercase tracking-wide text-text-subtle">
        {SOURCE_TITLES[item.source]}
      </p>
      {isPending ? (
        <Spinner className="size-4" />
      ) : error ? (
        <ErrorState error={error} title="Could not load the transcript" />
      ) : !data?.length ? (
        <p className="text-xs text-text-muted">This transcript can no longer be read.</p>
      ) : (
        <div className="max-h-96 space-y-0.5 overflow-y-auto pr-1 text-xs scrollbar-subtle">
          {data.map((segment) => (
            <Line key={segment.seq} segment={segment} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One line, drawn as the call's details draw a line they cannot seek from. */
function Line({ segment }: { segment: TranscriptSegment }) {
  const label = SPEAKER_LABELS[segment.speaker] ?? null;
  return (
    <p className="rounded px-1 py-0.5">
      <span className="mr-1.5 font-mono text-2xs tabular text-text-subtle">
        {transcriptStamp(segment.start_seconds)}
      </span>
      {label && (
        <span
          title={SPEAKER_HINTS[segment.speaker]}
          className={cn(
            "mr-1 font-medium",
            segment.speaker === "near" && "text-accent-11",
            segment.speaker === "far" && "text-success-11",
          )}
        >
          {label}:
        </span>
      )}
      <span className="text-text">{segment.text}</span>
    </p>
  );
}
